/**
 * WalletMonitorPanel.js
 * Live wallet monitor tab — add/remove watched addresses, start/stop polling,
 * and display a real-time SSE event feed.  New TXs that land while the panel
 * is open are surfaced immediately and can trigger a graph refresh.
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { monitorService } from '../services/api';
import '../styles/WalletMonitorPanel.css';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtTs(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch {
    return iso;
  }
}

function fmtVal(value, symbol) {
  if (value == null) return '—';
  const n = Number(value);
  const s = isNaN(n) ? String(value) : n.toFixed(6).replace(/\.?0+$/, '');
  return symbol ? `${s} ${symbol}` : s;
}

function shortAddr(addr) {
  if (!addr || addr.length < 12) return addr || '—';
  return `${addr.slice(0, 8)}…${addr.slice(-6)}`;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function WalletMonitorPanel({ investigationId, investigation, onNewTx, autoRefresh, onAutoRefreshChange, graphRefreshing, cascadeEnabled, onCascadeEnabledChange, cascadeDepth, onCascadeDepthChange, onMonitorsChange }) {
  const [monitors, setMonitors]         = useState([]);
  const [isRunning, setIsRunning]       = useState(false);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [actionBusy, setActionBusy]     = useState(false);

  // Auto-cascade settings — lifted to parent (InvestigationDetail) so they
  // persist across tab switches. cascadeEnabled/cascadeDepth come from props.
  const [cascadeBusy, setCascadeBusy] = useState(false);
  // Suppress the monitor_stopped / monitor_started pair the backend emits
  // when _on_cascade restarts the poll loop to add a new address.
  const suppressCascadeFlashRef = useRef(false);

  // Add-address form
  const [addAddress, setAddAddress] = useState('');
  const [addLabel, setAddLabel]     = useState('');
  const [addError, setAddError]     = useState(null);

  // Click-to-copy feedback — tracks which address (by a 'addr-<id>' /
  // 'parent-<id>' key) was just copied, so only that one row flashes
  // "Copied" instead of a global toast.
  const [copiedKey, setCopiedKey] = useState(null);
  const copiedTimerRef = useRef(null);

  // Auto-refresh is now controlled by parent (persists across tab switches)
  // graphRefreshing comes from parent too
  const autoRefreshRef = useRef(false); // ref so SSE handler always sees latest value

  // Keep ref in sync with prop
  useEffect(() => { autoRefreshRef.current = autoRefresh; }, [autoRefresh]);

  // Live event feed
  const [events, setEvents]           = useState([]);
  const [newTxCount, setNewTxCount]   = useState(0);
  const [streamState, setStreamState] = useState('disconnected'); // connected|disconnected|error
  const feedRef = useRef(null);
  const esRef   = useRef(null); // EventSource instance

  const pollInterval = 30; // seconds, mirrors POLL_INTERVAL in wallet_monitor.py

  // ── Load monitor list ──────────────────────────────────────────────────────
  const loadMonitors = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await monitorService.list(investigationId);
      setMonitors(res.data.monitors || []);
      setIsRunning(res.data.is_running || false);
      // Notify parent so graph can highlight monitored nodes
      onMonitorsChange?.((res.data.monitors || []).map(m => m.address).filter(Boolean));
      const cs = res.data.cascade || {};
      const enabled = !!cs.enabled;
      const depth = typeof cs.max_depth === 'number' ? cs.max_depth : 2;
      // Only seed parent state if it's still at defaults (first load);
      // if the user has already toggled cascade this session, leave it alone.
      if (!cascadeEnabled && enabled) onCascadeEnabledChange(enabled);
      if (cascadeDepth === 2 && depth !== 2) onCascadeDepthChange(depth);
    } catch (e) {
      setError('Failed to load monitors: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [investigationId]);

  useEffect(() => {
    loadMonitors();
  }, [loadMonitors]);

  // ── SSE connection ─────────────────────────────────────────────────────────
  const openStream = useCallback(() => {
    if (esRef.current) {
      esRef.current.close();
      esRef.current = null;
    }

    // Native EventSource can't set Authorization headers.
    // We append the token as a query param; the backend reads it the same way
    // FastAPI's Depends(get_current_user) does via oauth2_scheme (query fallback).
    const token = monitorService.getToken();
    const url   = `${monitorService.streamUrl(investigationId)}${token ? `?token=${encodeURIComponent(token)}` : ''}`;

    const es = new EventSource(url);
    esRef.current = es;

    es.onopen = () => setStreamState('connected');

    es.onmessage = async (e) => {
      try {
        const evt = JSON.parse(e.data);
        if (evt.type === 'keep-alive') return;

        setEvents(prev => [evt, ...prev].slice(0, 200)); // keep last 200

        if (evt.type === 'new_tx') {
          setNewTxCount(c => c + 1);
          if (autoRefreshRef.current && onNewTx) {
            onNewTx(evt);
          }
        }

        // Cascade restart emits a monitor_stopped immediately followed by
        // monitor_started — treat that pair as a no-op so the feed doesn't
        // flicker with spurious stop/start cards on every auto-hop.
        if (evt.type === 'cascade_added') {
          suppressCascadeFlashRef.current = true;
          // Reload the monitor list so depth badges update
          await loadMonitors();
        }
        if ((evt.type === 'monitor_stopped' || evt.type === 'monitor_started') &&
            suppressCascadeFlashRef.current) {
          suppressCascadeFlashRef.current = false;
          return; // swallow the event — don't add to feed
        }

        // Scroll feed to top
        if (feedRef.current) feedRef.current.scrollTop = 0;
      } catch (_) { /* parse error */ }
    };

    es.onerror = () => {
      setStreamState('error');
      es.close();
      esRef.current = null;
      // Retry after 10 s if monitor is still running
      setTimeout(() => {
        if (esRef.current === null) openStream();
      }, 10000);
    };
  }, [investigationId, onNewTx]);

  const closeStream = useCallback(() => {
    if (esRef.current) {
      esRef.current.close();
      esRef.current = null;
    }
    setStreamState('disconnected');
  }, []);

  // Connect/disconnect SSE when isRunning changes
  useEffect(() => {
    if (isRunning) {
      openStream();
    } else {
      closeStream();
    }
    return () => closeStream(); // cleanup on unmount
  }, [isRunning, openStream, closeStream]);

  // ── Actions ────────────────────────────────────────────────────────────────

  const handleAdd = async () => {
    setAddError(null);
    const addr = addAddress.trim();
    if (!addr) { setAddError('Address is required'); return; }
    if (addr.startsWith('0x') && addr.length !== 42) {
      setAddError(`Invalid EVM address — must be 42 characters (yours is ${addr.length}). Check for missing or extra characters.`);
      return;
    }

    try {
      setActionBusy(true);
      await monitorService.add(investigationId, addr, addLabel.trim());
      setAddAddress('');
      setAddLabel('');
      await loadMonitors();
    } catch (e) {
      setAddError(e.response?.data?.detail || e.message);
    } finally {
      setActionBusy(false);
    }
  };

  const handleCopy = useCallback(async (text, key) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch (_) {
      // Clipboard API can fail in a non-secure/non-focused context — the click
      // target itself has already given the user the full address via title,
      // so just skip the confirmation flash rather than showing an error.
      return;
    }
    setCopiedKey(key);
    if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
    copiedTimerRef.current = setTimeout(() => setCopiedKey(null), 1500);
  }, []);

  useEffect(() => () => { if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current); }, []);

  const handleRemove = async (monitorId) => {
    try {
      setActionBusy(true);
      await monitorService.remove(investigationId, monitorId);
      await loadMonitors();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setActionBusy(false);
    }
  };

  const handleCascadeToggle = async (enabled) => {
    onCascadeEnabledChange(enabled);
    try {
      setCascadeBusy(true);
      await monitorService.updateSettings(investigationId, enabled, cascadeDepth);
    } catch (e) {
      setError('Failed to save cascade settings: ' + (e.response?.data?.detail || e.message));
      onCascadeEnabledChange(!enabled); // revert
    } finally {
      setCascadeBusy(false);
    }
  };

  const handleCascadeDepth = async (depth) => {
    const clamped = Math.max(1, Math.min(10, depth));
    onCascadeDepthChange(clamped);
    try {
      setCascadeBusy(true);
      await monitorService.updateSettings(investigationId, cascadeEnabled, clamped);
    } catch (e) {
      setError('Failed to save cascade depth: ' + (e.response?.data?.detail || e.message));
    } finally {
      setCascadeBusy(false);
    }
  };

  const handleStart = async () => {
    try {
      setActionBusy(true);
      await monitorService.start(investigationId);
      setIsRunning(true);
      setNewTxCount(0);
      setEvents([]);
      // Auto-fetch blockchain data on start so the graph is always populated
      if (onNewTx) onNewTx();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setActionBusy(false);
    }
  };

  const handleStop = async () => {
    try {
      setActionBusy(true);
      await monitorService.stop(investigationId);
      setIsRunning(false);
      closeStream();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setActionBusy(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) return <div className="monitor-loading">Loading monitors…</div>;

  const blockchain = investigation?.blockchain || '—';

  return (
    <div className="monitor-panel">

      {/* ── Header strip ── */}
      <div className="monitor-header">
        <div className="monitor-header-left">
          <span className="monitor-title">📡 Live Wallet Monitor</span>
          <span className="monitor-chain-badge">{blockchain}</span>
          {isRunning ? (
            <span className="monitor-status running">● Live</span>
          ) : (
            <span className="monitor-status stopped">● Stopped</span>
          )}
          {isRunning && (
            <span className="monitor-stream-state" data-state={streamState}>
              {streamState === 'connected'    && '🔗 Stream connected'}
              {streamState === 'disconnected' && '⏳ Connecting…'}
              {streamState === 'error'        && '⚠ Stream error — retrying'}
            </span>
          )}
        </div>
        <div className="monitor-header-right">
          <label className="monitor-toggle-label">
            <input
              type="checkbox"
              className="monitor-toggle-checkbox"
              checked={autoRefresh}
              onChange={e => onAutoRefreshChange(e.target.checked)}
            />
            <span className={`monitor-toggle-text${autoRefresh ? ' active' : ''}`}>
              {graphRefreshing ? '⏳ Refreshing graph…' : '🔄 Auto-refresh graph'}
            </span>
          </label>
          {newTxCount > 0 && (
            <span className="monitor-new-badge">{newTxCount} new TX{newTxCount !== 1 ? 's' : ''}</span>
          )}
          {isRunning ? (
            <button
              className="monitor-btn danger"
              onClick={handleStop}
              disabled={actionBusy}
            >⏹ Stop</button>
          ) : (
            <button
              className="monitor-btn primary"
              onClick={handleStart}
              disabled={actionBusy || monitors.length === 0}
              title={monitors.length === 0 ? 'Add at least one address first' : ''}
            >▶ Start</button>
          )}
        </div>
      </div>

      {error && <div className="monitor-error">{error}</div>}

      <div className="monitor-body">

        {/* ── Left column: watched addresses ── */}
        <div className="monitor-left">

          {/* Add address form */}
          <div className="monitor-add-card">
            <h3>Watch Address</h3>
            <div className="monitor-add-row">
              <input
                className="monitor-input"
                placeholder="0x… or address"
                value={addAddress}
                onChange={e => setAddAddress(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAdd()}
              />
              <input
                className="monitor-input label"
                placeholder="Label (optional)"
                value={addLabel}
                onChange={e => setAddLabel(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAdd()}
              />
              <button
                className="monitor-btn primary"
                onClick={handleAdd}
                disabled={actionBusy}
              >+ Add</button>
            </div>
            {addError && <div className="monitor-add-error">{addError}</div>}
          </div>

          {/* Watched address list */}
          <div className="monitor-watch-list">
            <h3>Watched Addresses <span className="monitor-count">{monitors.length}</span></h3>
            {monitors.length === 0 ? (
              <div className="monitor-empty">
                No addresses being monitored yet.<br />Add one above to get started.
              </div>
            ) : (
              monitors.map(m => (
                <div key={m.id} className={`monitor-watch-item${m.cascade ? ' cascade-item' : ''}`}>
                  <div className="monitor-watch-info">
                    <div className="monitor-watch-label-row">
                      <span className="monitor-watch-label">{m.label}</span>
                      {m.cascade ? (
                        <span className="monitor-depth-badge" title={`Auto-added at hop depth ${m.depth} from ${m.parent_address || '?'}`}>
                          ⛓ depth {m.depth}
                        </span>
                      ) : (
                        <span className="monitor-root-badge">root</span>
                      )}
                    </div>
                    <button
                      type="button"
                      className="monitor-watch-addr monitor-copy-btn"
                      title={`${m.address} — click to copy`}
                      onClick={() => handleCopy(m.address, `addr-${m.id}`)}
                    >
                      <span className="monitor-watch-addr-text">{shortAddr(m.address)}</span>
                      <span className="monitor-copy-icon">
                        {copiedKey === `addr-${m.id}` ? '✓ Copied' : '⧉ Copy'}
                      </span>
                      {m.address?.startsWith('0x') && m.address.length !== 42 && (
                        <span style={{ color: '#f87171', marginLeft: 6, fontSize: 10 }}>
                          ⚠ {m.address.length} chars
                        </span>
                      )}
                    </button>
                    {m.parent_address && (
                      <button
                        type="button"
                        className="monitor-watch-parent monitor-copy-btn-inline"
                        title={`${m.parent_address} — click to copy`}
                        onClick={() => handleCopy(m.parent_address, `parent-${m.id}`)}
                      >
                        ↳ from {shortAddr(m.parent_address)}
                        {copiedKey === `parent-${m.id}` ? ' ✓' : ''}
                      </button>
                    )}
                    <span className="monitor-watch-since">Since {fmtTs(m.added_at) || '—'}</span>
                  </div>
                  <button
                    className="monitor-btn remove"
                    onClick={() => handleRemove(m.id)}
                    disabled={actionBusy}
                    title="Remove from watch list"
                  >✕</button>
                </div>
              ))
            )}
          </div>

          {/* Auto-cascade settings card */}
          <div className="monitor-cascade-card">
            <h3>Auto-Cascade <span className="monitor-cascade-tag">beta</span></h3>
            <p className="monitor-cascade-desc">
              When enabled, any outbound TX from a watched address automatically
              adds the recipient as a new watched address one hop deeper.
            </p>
            <div className="monitor-cascade-row">
              <label className="monitor-toggle-label">
                <input
                  type="checkbox"
                  className="monitor-toggle-checkbox"
                  checked={cascadeEnabled}
                  onChange={e => handleCascadeToggle(e.target.checked)}
                  disabled={cascadeBusy}
                />
                <span className={`monitor-toggle-text${cascadeEnabled ? ' active' : ''}`}>
                  {cascadeEnabled ? '⛓ Cascade enabled' : 'Enable cascade'}
                </span>
              </label>
            </div>
            {cascadeEnabled && (
              <div className="monitor-cascade-depth-row">
                <span className="monitor-cascade-depth-label">Max hop depth</span>
                <div className="monitor-cascade-stepper">
                  <button
                    className="monitor-stepper-btn"
                    onClick={() => handleCascadeDepth(cascadeDepth - 1)}
                    disabled={cascadeBusy || cascadeDepth <= 1}
                  >−</button>
                  <span className="monitor-stepper-val">{cascadeDepth}</span>
                  <button
                    className="monitor-stepper-btn"
                    onClick={() => handleCascadeDepth(cascadeDepth + 1)}
                    disabled={cascadeBusy || cascadeDepth >= 10}
                  >+</button>
                </div>
                <span className="monitor-cascade-depth-hint">hard cap: 10</span>
              </div>
            )}
          </div>

          {/* Info box */}
          <div className="monitor-info-box">
            <strong>How it works</strong>
            <p>
              When running, the server polls each watched address every {pollInterval}s
              for new transactions. New TXs appear in the live feed on the right and
              the graph is updated automatically.
            </p>
            <p style={{ color: '#6b7280', fontSize: 11 }}>
              Only transactions confirmed in the last 10 minutes are surfaced as "new"
              — historical TXs are seeded into the seen-cache on first poll.
            </p>
          </div>
        </div>

        {/* ── Right column: event feed ── */}
        <div className="monitor-right">
          <div className="monitor-feed-header">
            <h3>Live Event Feed</h3>
            {events.length > 0 && (
              <button
                className="monitor-btn ghost"
                onClick={() => { setEvents([]); setNewTxCount(0); }}
              >Clear</button>
            )}
          </div>

          <div className="monitor-feed" ref={feedRef}>
            {events.length === 0 ? (
              <div className="monitor-feed-empty">
                {isRunning
                  ? `Waiting for new transactions… (polling every ${pollInterval}s)`
                  : 'Start the monitor to see live events here.'}
              </div>
            ) : (
              events.map((evt, i) => (
                <div key={i} className={`monitor-event monitor-event-${evt.type}`}>
                  {evt.type === 'new_tx' && (
                    <>
                      <span className="monitor-event-icon">💸</span>
                      <div className="monitor-event-body">
                        <div className="monitor-event-title">
                          New TX on {evt.address}
                        </div>
                        <div className="monitor-event-detail">
                          <span className="monitor-event-addrs">{evt.from_address} &rarr; {evt.to_address}</span>
                          <span className="monitor-event-value">{fmtVal(evt.value, evt.token_symbol)}</span>
                        </div>
                        <div className="monitor-event-meta">
                          <span className="monitor-event-hash" title={evt.tx_hash}>{evt.tx_hash}</span>
                          <span className="monitor-event-time">{fmtTs(evt.timestamp)}</span>
                        </div>
                      </div>
                    </>
                  )}
                  {evt.type === 'heartbeat' && (
                    <>
                      <span className="monitor-event-icon hb">♡</span>
                      <div className="monitor-event-body">
                        <span className="monitor-event-title">Heartbeat</span>
                        <span className="monitor-event-time">{fmtTs(evt.ts)}</span>
                      </div>
                    </>
                  )}
                  {evt.type === 'monitor_started' && (
                    <>
                      <span className="monitor-event-icon start">▶</span>
                      <div className="monitor-event-body">
                        <span className="monitor-event-title">Monitor started — {evt.addresses?.length || 0} address(es)</span>
                      </div>
                    </>
                  )}
                  {evt.type === 'monitor_stopped' && (
                    <>
                      <span className="monitor-event-icon stop">⏹</span>
                      <div className="monitor-event-body">
                        <span className="monitor-event-title">Monitor stopped</span>
                      </div>
                    </>
                  )}
                  {evt.type === 'cascade_added' && (
                    <>
                      <span className="monitor-event-icon cascade">⛓</span>
                      <div className="monitor-event-body">
                        <div className="monitor-event-title cascade-title">
                          Auto-cascade: new address at depth {evt.depth}
                        </div>
                        <div className="monitor-event-detail">
                          <span>{shortAddr(evt.address)}</span>
                          {evt.parent_address && (
                            <span style={{ color: '#6b7280' }}>from {shortAddr(evt.parent_address)}</span>
                          )}
                        </div>
                        {evt.tx_hash && (
                          <div className="monitor-event-meta">
                            <span className="monitor-event-hash" title={evt.tx_hash}>{shortAddr(evt.tx_hash)}</span>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                  {evt.type === 'connected' && (
                    <>
                      <span className="monitor-event-icon start">🔗</span>
                      <div className="monitor-event-body">
                        <span className="monitor-event-title">Stream connected</span>
                      </div>
                    </>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
